import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    NavigateBefore as NavigateBeforeIcon,
    NavigateNext as NavigateNextIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { getWeekTimecards } from '../../../services/timecardService';
import { useAuth } from '../../../context/AuthContext';
import ExportButton from '../../../components/common/ExportButton';
import { getWeekInfo } from '../../../utils/dateUtils';

// Utility functions
const getLocalDateString = (date) => {
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().split('T')[0];
};

const getLocalSunday = (date) => {
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    const day = localDate.getDay();
    const diff = localDate.getDate() - day;
    return new Date(localDate.setDate(diff));
};

const utcToLocal = (utcDateString) => {
    const date = new Date(utcDateString);
    return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
};

const getWeekNumber = (date) => {
    // Use the same week number calculation as Timecard.js
    return format(date, 'w');
};

const WeeklyViewAll = ({ onBack }) => {
    const [dataLoading, setDataLoading] = useState(false);
    const [error, setError] = useState(null);
    const [timecards, setTimecards] = useState([]);
    const [currentDate, setCurrentDate] = useState(new Date());
    const { user, loading: authLoading } = useAuth();

    // Memoize currentSunday to prevent unnecessary recalculations
    const currentSunday = useMemo(() => getLocalSunday(currentDate), [currentDate]);
    
    // Memoize week number
    const weekNumber = useMemo(() => getWeekNumber(currentSunday), [currentSunday]);

    // Fetch timecards when date changes
    useEffect(() => {
        let mounted = true;
        const controller = new AbortController();

        const fetchTimecardData = async () => {
            try {
                setDataLoading(true);
                const dateString = getLocalDateString(currentSunday);
                
                console.log('Fetching timecards for all users:', {
                    dateString,
                    weekNumber,
                    userRole: user?.role,
                    currentSunday: currentSunday.toISOString(),
                    currentSundayUTC: new Date(Date.UTC(
                        currentSunday.getFullYear(),
                        currentSunday.getMonth(),
                        currentSunday.getDate(),
                        0, 0, 0, 0
                    )).toISOString()
                });
                
                // Explicitly request all users' data
                const data = await getWeekTimecards(dateString, null, 'all');
                if (mounted) {
                    console.log('Timecards received:', {
                        count: data.length,
                        weekNumber,
                        timecards: data.map(tc => ({
                            id: tc._id,
                            user: tc.user?.username || 'Unknown',
                            project: tc.project?.name || 'Unknown',
                            startDate: tc.startDate,
                            endDate: tc.endDate,
                            days: tc.days.map(d => ({
                                date: d.date,
                                hours: d.hours
                            }))
                        }))
                    });
                    
                    // Filter timecards based on user role
                    let filteredTimecards = [...data];
                    
                    // If user is a team lead, filter to only show their team members
                    if (user.role === 'team_lead') {
                        console.log('User is a team lead, filtering timecards');
                        
                        // Get team member IDs
                        let teamMemberIds = [];
                        
                        // Check if user has teamMembers directly
                        if (user.teamMembers && user.teamMembers.length > 0) {
                            console.log('Using teamMembers array directly:', user.teamMembers);
                            teamMemberIds = user.teamMembers.map(id => String(id));
                            // Add the team lead to the list
                            if (!teamMemberIds.includes(String(user.id))) {
                                teamMemberIds.push(String(user.id));
                            }
                        } else if (user.teams && user.teams.length > 0) {
                            // Get only the teams where the user is the team lead
                            const leadTeams = user.teams.filter(team => 
                                String(team.teamLead) === String(user.id)
                            );
                            
                            if (leadTeams.length > 0) {
                                // Get all members from these teams
                                const teamMembers = leadTeams.flatMap(team => team.members || []);
                                
                                // Add the team lead to the list of members if not already included
                                if (!teamMembers.includes(user.id) && !teamMembers.some(id => String(id) === String(user.id))) {
                                    teamMembers.push(user.id);
                                }
                                
                                // Make sure we don't have duplicates
                                teamMemberIds = [...new Set(teamMembers.map(id => String(id)))];
                            } else {
                                // If user is not a team lead for any team, they can only see themselves
                                teamMemberIds = [String(user.id)];
                            }
                        } else {
                            // If user has no teams or teamMembers, they can only see themselves
                            teamMemberIds = [String(user.id)];
                        }
                        
                        console.log('Team member IDs:', teamMemberIds);
                        
                    // Filter timecards to only show those for team members
                    filteredTimecards = filteredTimecards.filter(timecard => 
                        timecard.user && teamMemberIds.includes(String(timecard.user._id))
                    );
                    
                    console.log('Filtered timecards count:', filteredTimecards.length);
                    console.log('Detailed timecard info:', filteredTimecards.map(tc => ({
                        id: tc._id,
                        user: tc.user?.username || 'Unknown',
                        project: tc.project?.name || 'Unknown',
                        startDate: tc.startDate,
                        endDate: tc.endDate,
                        weekNumber: weekNumber,
                        days: tc.days.map(d => ({
                            date: d.date,
                            dayOfWeek: new Date(d.date).getUTCDay() // 0 = Sunday
                        }))
                    })));
                    }
                    
                    // Additional filtering to ensure timecards are only shown in the correct week
                    const weekFilteredTimecards = filteredTimecards.filter(timecard => {
                        // Calculate the week number for this timecard's start date
                        const timecardStartDate = new Date(timecard.startDate);
                        const timecardWeekInfo = getWeekInfo(timecardStartDate);
                        
                        // Instead of comparing week numbers (which can be inconsistent),
                        // check if the timecard's date range overlaps with the current week's date range
                        
                        // Get the current week's date range
                        const currentWeekStart = new Date(currentSunday);
                        const currentWeekEnd = new Date(currentSunday);
                        currentWeekEnd.setDate(currentWeekEnd.getDate() + 6);
                        
                        // Get the timecard's start and end dates
                        const tcStartDate = new Date(timecard.startDate);
                        const tcEndDate = new Date(timecard.endDate);
                        
                        // A timecard belongs to this week if its date range overlaps with the current week's date range
                        // This is true if:
                        // 1. The timecard's start date is within the current week, OR
                        // 2. The timecard's end date is within the current week, OR
                        // 3. The timecard spans the entire current week
                        const isCorrectWeek = 
                            // Case 1: Timecard starts within the week
                            (tcStartDate >= currentWeekStart && tcStartDate <= currentWeekEnd) ||
                            // Case 2: Timecard ends within the week
                            (tcEndDate >= currentWeekStart && tcEndDate <= currentWeekEnd) ||
                            // Case 3: Timecard spans the entire week
                            (tcStartDate <= currentWeekStart && tcEndDate >= currentWeekEnd);
                        
                        console.log('Frontend timecard week check:', {
                            id: timecard._id,
                            user: timecard.user?.username || 'Unknown',
                            timecardWeekNumber: timecardWeekInfo.weekNumber,
                            timecardYear: timecardWeekInfo.year,
                            currentWeekNumber: weekNumber,
                            isCorrectWeek
                        });
                        
                        return isCorrectWeek;
                    });
                    
                    console.log('Week timecards (after frontend filtering):', {
                        beforeCount: filteredTimecards.length,
                        afterCount: weekFilteredTimecards.length,
                        weekNumber
                    });
                    
                    setTimecards(weekFilteredTimecards);
                    setError(null);
                }
            } catch (err) {
                if (err.name === 'AbortError') {
                    console.log('Fetch aborted');
                    return;
                }
                console.error('Error fetching timecards:', err);
                if (mounted) {
                    setError(err.message);
                    setTimecards([]);
                }
            } finally {
                if (mounted) {
                    setDataLoading(false);
                }
            }
        };

        // Only fetch if we have a user
        if (user) {
            fetchTimecardData();
        }

        return () => {
            mounted = false;
            controller.abort();
        };
    }, [currentSunday, weekNumber, user]);

    const handlePreviousWeek = () => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setDate(newDate.getDate() - 7);
            return newDate;
        });
    };

    const handleNextWeek = () => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setDate(newDate.getDate() + 7);
            return newDate;
        });
    };


    // Memoize week info calculation
    const weekInfo = useMemo(() => {
        // Always base the week on currentDate, not timecard data
        const days = Array.from({ length: 7 }, (_, i) => {
            const date = new Date(currentSunday);
            date.setDate(currentSunday.getDate() + i);
            return {
                date: date.toISOString()
            };
        });
        
        return { days };
    }, [currentSunday]);

    const { days } = weekInfo;

    // Show loading state while auth is loading
    if (authLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    // Show error if no user data
    if (!user) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Alert severity="error">Authentication required</Alert>
            </Box>
        );
    }

    console.log('Rendering week for all users:', {
        weekNumber,
        currentSunday: currentSunday.toISOString(),
        timecardCount: timecards.length
    });

    // Get date range for filename
    const dateRange = timecards.length > 0 
        ? `${format(utcToLocal(timecards[0].days[0].date), 'MMM_d', { locale: enUS })}_to_${format(utcToLocal(timecards[0].days[timecards[0].days.length - 1].date), 'MMM_d', { locale: enUS })}`
        : `${format(currentSunday, 'MMM_d')}_to_${format(new Date(days[days.length - 1].date), 'MMM_d')}`;

    return (
        <Box sx={{ 
            p: 3,
            '@media (max-width:600px)': {
                p: 1, // Much more reduced padding for mobile
                paddingTop: 0, // No top padding - layout provides spacing
                paddingBottom: '88px', // Account for bottom navigation
            },
            '@media (max-width:900px) and (orientation: landscape)': {
                p: 0.5, // Minimal padding in landscape mode
                paddingTop: 0, // No top padding
                paddingBottom: 0.5, // Minimal bottom padding
                height: 'auto', // Allow natural height
                overflow: 'visible', // Allow content to overflow
                minHeight: 'auto', // Remove height constraints
            }
        }}>
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Header */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 3,
                '@media (max-width:600px)': {
                    mb: 1, // Much more reduced margin for mobile
                },
                '@media (max-width:900px) and (orientation: landscape)': {
                    mb: 0.5, // Reduced margin in landscape mode but keep header visible
                }
            }}>
                <IconButton onClick={onBack} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6">
                    Weekly View - All Users
                </Typography>
            </Box>

            {/* Navigation Bar */}
            <Paper sx={{ 
                p: 2, 
                mb: 3,
                '@media (max-width:600px)': {
                    p: 0, // Remove all padding on mobile
                    mb: 1.5,
                }
            }}>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    '@media (max-width:600px)': {
                        flexDirection: 'row', // Keep on same line for mobile
                        gap: 0.5,
                        alignItems: 'center',
                        px: 0.5, // Add minimal padding inside the flex container
                        py: 1,
                    }
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        '@media (max-width:600px)': {
                            flex: 1,
                            gap: 0.5,
                            marginLeft: '-8px', // Move the entire week navigation widget to the left
                        }
                    }}>
                        <IconButton 
                            onClick={handlePreviousWeek}
                            sx={{
                                '@media (max-width:600px)': {
                                    padding: '4px',
                                }
                            }}
                        >
                            <NavigateBeforeIcon />
                        </IconButton>
                        <Typography sx={{
                            '@media (max-width:600px)': {
                                fontSize: '0.75rem',
                                textAlign: 'center',
                                flex: 1,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }
                        }}>
                            Week {weekNumber}: {format(currentSunday, 'MMM d', { locale: enUS })} - {format(new Date(days[days.length - 1].date), 'MMM d, yyyy', { locale: enUS })}
                        </Typography>
                        <IconButton 
                            onClick={handleNextWeek}
                            sx={{
                                '@media (max-width:600px)': {
                                    padding: '4px',
                                }
                            }}
                        >
                            <NavigateNextIcon />
                        </IconButton>
                    </Box>
                    <ExportButton
                        viewType="weekly"
                        timecards={timecards}
                        viewMode="allUsers"
                        isAllUsers={true}
                        user={{ username: 'all_users' }}
                        date={dateRange}
                        mobileIconOnly={true}
                    />
                </Box>
            </Paper>

            {/* Timecard Table */}
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: '#e0e0e0' }}>
                        <TableRow>
                            <TableCell sx={{ 
                                width: '9%',
                                '@media (max-width:900px)': {
                                    width: '15%',
                                    fontSize: '0.75rem',
                                    padding: '8px',
                                }
                            }}>Username</TableCell>
                            <TableCell sx={{ 
                                width: '12%',
                                '@media (max-width:900px)': {
                                    width: '20%',
                                    fontSize: '0.75rem',
                                    padding: '8px',
                                }
                            }}>Project</TableCell>
                            <TableCell sx={{ 
                                width: '9%', 
                                whiteSpace: 'nowrap',
                                '@media (max-width:900px)': {
                                    width: '25%',
                                    fontSize: '0.75rem',
                                    padding: '8px',
                                    minWidth: '100px',
                                }
                            }}>Date Range</TableCell>
                            {days.map((day, index) => {
                                const date = new Date(day.date);
                                return (
                                    <TableCell 
                                        key={index} 
                                        align="center" 
                                        sx={{ 
                                            width: '10%',
                                            '@media (max-width:900px)': {
                                                fontSize: '0.7rem',
                                                padding: '4px',
                                                minWidth: '50px',
                                            }
                                        }}
                                    >
                                        <Box sx={{
                                            display: 'block',
                                            '@media (max-width:900px)': {
                                                display: 'none',
                                            }
                                        }}>
                                            {format(date, 'EEE', { locale: enUS })}<br />
                                            {format(date, 'MMM d', { locale: enUS })}
                                        </Box>
                                        <Box sx={{
                                            display: 'none',
                                            '@media (max-width:900px)': {
                                                display: 'block',
                                            }
                                        }}>
                                            {format(date, 'M/d', { locale: enUS })}
                                        </Box>
                                    </TableCell>
                                );
                            })}
                            <TableCell align="center" sx={{
                                '@media (max-width:900px)': {
                                    fontSize: '0.75rem',
                                    padding: '8px',
                                }
                            }}>Total</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {timecards.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={11} align="center">
                                    No timecard data found for this week
                                </TableCell>
                            </TableRow>
                        ) : (
                            timecards.map((timecard) => (
                                <React.Fragment key={timecard._id}>
                                    <TableRow>
                                        <TableCell sx={{
                                            '@media (max-width:900px)': {
                                                fontSize: '0.75rem',
                                                padding: '8px',
                                            }
                                        }}>{timecard.user.username}</TableCell>
                                        <TableCell sx={{
                                            '@media (max-width:900px)': {
                                                fontSize: '0.75rem',
                                                padding: '8px',
                                            }
                                        }}>{timecard.project.name}</TableCell>
                                        <TableCell sx={{
                                            whiteSpace: 'nowrap',
                                            '@media (max-width:900px)': {
                                                fontSize: '0.75rem',
                                                whiteSpace: 'nowrap',
                                                padding: '8px',
                                            }
                                        }}>
                                            <Box sx={{
                                                display: 'block',
                                                '@media (max-width:900px)': {
                                                    display: 'none',
                                                }
                                            }}>
                                                {format(utcToLocal(timecard.days[0].date), 'MMM d', { locale: enUS })} - {format(utcToLocal(timecard.days[timecard.days.length - 1].date), 'MMM d', { locale: enUS })}
                                            </Box>
                                            <Box sx={{
                                                display: 'none',
                                                '@media (max-width:900px)': {
                                                    display: 'block',
                                                }
                                            }}>
                                                {format(utcToLocal(timecard.days[0].date), 'M/d', { locale: enUS })} - {format(utcToLocal(timecard.days[timecard.days.length - 1].date), 'M/d', { locale: enUS })}
                                            </Box>
                                        </TableCell>
                                        {timecard.days.map((day, index) => (
                                            <TableCell key={index} align="center" sx={{
                                                '@media (max-width:900px)': {
                                                    fontSize: '0.75rem',
                                                    padding: '4px',
                                                }
                                            }}>
                                                {parseFloat(day.hours).toFixed(2)}
                                            </TableCell>
                                        ))}
                                        <TableCell align="center" sx={{
                                            '@media (max-width:900px)': {
                                                fontSize: '0.75rem',
                                                padding: '8px',
                                            }
                                        }}>
                                            {timecard.days.reduce((sum, day) => sum + parseFloat(day.hours), 0).toFixed(2)}
                                        </TableCell>
                                    </TableRow>
                                    {/* Comments row */}
                                    {timecard.days.some(day => day.comments) && (
                                        <TableRow>
                                            <TableCell />
                                            <TableCell />
                                            <TableCell />
                                            {timecard.days.map((day, index) => (
                                                <TableCell 
                                                    key={index} 
                                                    align="center"
                                                    sx={{ 
                                                        whiteSpace: 'pre-wrap',
                                                        fontSize: '0.875rem',
                                                        color: 'text.secondary'
                                                    }}
                                                >
                                                    {day.comments || ''}
                                                </TableCell>
                                            ))}
                                            <TableCell />
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                        {/* Total row */}
                        {timecards.length > 0 && (
                            <TableRow sx={{ bgcolor: '#f8f8d7' }}>
                                <TableCell>Total</TableCell>
                                <TableCell />
                                <TableCell />
                                {days.map((_, index) => {
                                    const dayTotal = timecards.reduce((sum, timecard) => {
                                        return sum + parseFloat(timecard.days[index].hours);
                                    }, 0);
                                    return (
                                        <TableCell key={index} align="center">
                                            {dayTotal.toFixed(2)}
                                        </TableCell>
                                    );
                                })}
                                <TableCell align="center">
                                    {timecards.reduce((sum, timecard) => {
                                        return sum + timecard.days.reduce((daySum, day) => 
                                            daySum + parseFloat(day.hours), 0);
                                    }, 0).toFixed(2)}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            
            {dataLoading && (
                <Box sx={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    right: 0, 
                    bottom: 0, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    zIndex: 1000
                }}>
                    <CircularProgress />
                </Box>
            )}
        </Box>
    );
};

export default WeeklyViewAll;
